import * as React from 'react';
import { useIntl } from 'react-intl';

import { CURRENCY } from 'src/types';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import { REFERENCE_RATE_UNIT_DECIMALS } from 'src/features/referenceRates/utils/constants';
import { REFERENCE_RATE_UNIT } from 'src/features/referenceRates/utils/types';
import { fromReferenceRateMinorUnits } from 'src/features/referenceRates/utils/units';

type ReferenceRateUnitInfo = {
    unit?: REFERENCE_RATE_UNIT | null;
    currency?: CURRENCY | null;
};

/**
 * Formats a stored `ReferenceRateValue.amount` for display with its unit: "103,50 €", "3,5 %", "1,035 ×",
 * "103,5 пункта". The returned function is stable for a given locale, so it is safe to call from table cells.
 */
export function useFormatReferenceRateAmount() {
    const { formatNumber, formatMessage } = useIntl();

    return React.useCallback(
        (amount: number, { unit, currency }: ReferenceRateUnitInfo): string => {
            const resolvedUnit = unit ?? REFERENCE_RATE_UNIT.CURRENCY;
            const value = fromReferenceRateMinorUnits(amount, resolvedUnit);

            if (resolvedUnit === REFERENCE_RATE_UNIT.CURRENCY) {
                return currency
                    ? formatNumber(value, { style: 'currency', currency })
                    : formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }

            const number = formatNumber(value, { maximumFractionDigits: REFERENCE_RATE_UNIT_DECIMALS[resolvedUnit] });

            switch (resolvedUnit) {
                case REFERENCE_RATE_UNIT.PERCENT:
                    return `${number} %`;
                case REFERENCE_RATE_UNIT.COEFFICIENT:
                    return `${number} \u00d7`;
                case REFERENCE_RATE_UNIT.POINTS:
                    return formatMessage(referenceRatesMessages.pointsValue, { value: number });
            }
        },
        [formatMessage, formatNumber],
    );
}
